import "@/components/TaskTimer/taskTimer.css";
import { AppContext } from "@/services/AppContext/appContext";
import { useTheme } from "@/shadcn/components/theme-provider";
import { Button } from "@/shadcn/components/ui/button";
import { CirclePlay, CircleStop, Plus } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import CreateTimerDialog from "../CreateTimer/createTimerDialog.component";

type TaskTimerProps = {
  type:
    | { location: "navbar" }
    | { location: "navbar-dropdown" }
    | { location: "task" | "allTasks"; taskID: number; taskName: string };
};

const TaskTimer: React.FC<TaskTimerProps> = ({ type }) => {
  const {
    startTimer,
    stopTimer,
    resetTimer,
    runningTimer,
    sendNewTimerStart,
    sendUpdateTimer,
    userTimers,
    continueTimer,
  } = useContext(AppContext);

  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const calculateElapsedTime = () => {
      if (type.location === "navbar" || type.location === "navbar-dropdown") {
        if (!runningTimer) {
          setElapsedTime(0);
          return;
        }
        // Calculate elapsed time for running timer
        const initialElapsedTime = runningTimer.events.reduce(
          (total, event) => {
            const start = new Date(event.start).getTime();
            const end = event.end ? new Date(event.end).getTime() : Date.now();
            return total + (end - start);
          },
          0
        );
        setElapsedTime(Math.floor(initialElapsedTime / 1000));
      } else if (type.location === "task" || type.location === "allTasks") {
        // Find the timer for this task
        const taskTimer = userTimers.find(
          (timer) => timer.task_id === type.taskID
        );
        if (!taskTimer) {
          setElapsedTime(0);
          return;
        }
        // Calculate total elapsed time for this task
        const totalElapsedTime = taskTimer.events.reduce((total, event) => {
          const start = new Date(event.start).getTime();
          const end = event.end
            ? new Date(event.end).getTime()
            : runningTimer && runningTimer.id === taskTimer.id
              ? Date.now()
              : start;
          return total + (end - start);
        }, 0);
        setElapsedTime(Math.floor(totalElapsedTime / 1000));
      }
    };

    calculateElapsedTime();

    // Start the timer if it's running
    if (
      runningTimer &&
      (type.location === "navbar" ||
        type.location === "navbar-dropdown" ||
        ((type.location === "allTasks" || type.location === "task") &&
          runningTimer.task_id === type.taskID))
    ) {
      timerIntervalRef.current = setInterval(() => {
        calculateElapsedTime();
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [runningTimer, type, userTimers]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  async function startUTimer() {
    if (type.location === "allTasks" || type.location === "task") {
      sendNewTimerStart({
        taskID: type.taskID,
        timerName: type.taskName,
      });
    } else {
      sendNewTimerStart({
        taskID: null,
        timerName: null,
      });
    }
  }

  function continueStartUTaskTimer(taskID: number) {
    const timer = userTimers.find((timer) => timer.task_id === taskID);
    if (!timer) {
      console.log("Timer not found");
      // start a new timer
      startUTimer();
      return;
    }

    continueTimer({
      id: timer.id,
      startTime: new Date().toISOString(),
    });
  }

  return (
    <div>
      {type.location === "navbar" ? (
        <div className="hidden lg:flex w-56 xl:w-60 mr-2 lg:justify-end max-w-full">
          {runningTimer ? (
            <div className="mr-4 max-w-full">
              <div className="flex justify-end items-center max-w-full">
                <div className="truncate w-auto max-w-full">
                  {formatTime(elapsedTime)} - {runningTimer.name}
                </div>
                <CircleStop
                  onClick={(e) => {
                    e.stopPropagation();
                    stopTimer();
                  }}
                  className="fill-primary cursor-pointer ms-1 h-6 w-6 dark:h-7 dark:w-7 shrink-0 stroke-white timer-circle-pause m-auto"
                />
              </div>
            </div>
          ) : (
            <div className="text-right mr-2">
              <CreateTimerDialog>
                <Button
                  variant={theme === "light" ? "secondary" : "default"}
                  className={`rounded-full items-center justify-center hidden lg:flex`}
                >
                  Timer <Plus className="h-5 ml-1" />
                </Button>
              </CreateTimerDialog>
            </div>
          )}
        </div>
      ) : type.location === "navbar-dropdown" ? (
        runningTimer && (
          <div className="text-center mt-1 mb-3 lg:hidden">
            <div>{formatTime(elapsedTime)}</div>
            <div className="truncate">{runningTimer.name}</div>
            <CircleStop
              onClick={(e) => {
                e.stopPropagation();
                stopTimer();
              }}
              className="fill-primary cursor-pointer m-auto h-8 shrink-0 stroke-white timer-circle-pause"
            />
          </div>
        )
      ) : type.location === "allTasks" || type.location === "task" ? (
        <div
          className={`flex items-center select-none ${
            type.location === "allTasks" && "flex-row justify-end"
          }`}
        >
          {formatTime(elapsedTime)}
          {runningTimer && runningTimer.task_id === type.taskID ? (
            <>
              <CircleStop
                onClick={(e) => {
                  e.stopPropagation();
                  stopTimer();
                }}
                className="fill-primary cursor-pointer ms-1 h-7 w-7 stroke-white timer-circle-pause page-timer-circle-pause"
              />
              {/* <TimerReset
                onClick={resetTimer}
                className="fill-primary cursor-pointer ms-1 h-8"
              /> */}
            </>
          ) : (
            <CirclePlay
              onClick={(e) => {
                e.stopPropagation();
                continueStartUTaskTimer(type.taskID);
              }}
              className="fill-primary cursor-pointer ms-1 h-7 w-7 timer-circle-play stroke-white"
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default TaskTimer;
